import type { SupSingleVaultV3 } from "@/types";
import type { Placement } from "@popperjs/core/lib/enums";
import React, { useCallback } from "react";

import UiLabelApy, { type SupDicApyPeriod } from "@/components/Ui/Label/Apy";
import { useStoredData, useStoredDataDispatch } from "@/store";
import UiLabelRewardsTooltip from "@/components/Ui/Label/Rewards/Tooltip";
import { SupAssetLabel, SupHeading, SupTooltip } from "@repo/core-ui";
import { SupMint } from "@repo/core-ui";
import { twMerge } from "tailwind-merge";

interface UiLabelRewardsTagProps {
  useHeader?: boolean;
  showTooltip?: boolean;
  vault: SupSingleVaultV3;
  isFlexHeight?: boolean;
  unwrapped?: boolean;
  iconClass?: string;
  period?: SupDicApyPeriod;
  noAction?: boolean;
  useFallback?: boolean;
  tooltipPlacement?: Placement;
}

export default function UiLabelRewardsTag({
  useHeader,
  showTooltip = true,
  vault,
  isFlexHeight,
  iconClass,
  unwrapped,
  tooltipPlacement,
  period = "month",
  noAction = false,
  useFallback = false,
}: UiLabelRewardsTagProps) {
  const storedDispatch = useStoredDataDispatch();
  const storedData = useStoredData();
  const allowedAction = !noAction;

  const handleRewards = useCallback(() => {
    allowedAction && storedDispatch({ type: "setOverlay", overlay: "rewards-apy" });
  }, [allowedAction, storedDispatch]);

  const linkElement = (
    <div className={twMerge("inline-flex flex-none justify-center", allowedAction ? "cursor-pointer" : showTooltip && "cursor-help")} onClick={handleRewards}>
      <SupAssetLabel
        className={twMerge("w-fit items-center gap-0.5", isFlexHeight ? "h-full" : "h-8")}
        label={
          useHeader ? (
            <SupHeading variant="h3" weight="black">
              <UiLabelApy
                useFallback={useFallback}
                noAction={noAction}
                unwrapped={unwrapped}
                vault={vault}
                useNetApy={storedData.includeRewardsInApy}
                hideTooltip
                period={period}
              />
            </SupHeading>
          ) : (
            <UiLabelApy
              useFallback={useFallback}
              isBold
              noAction={noAction}
              vault={vault}
              unwrapped={unwrapped}
              useNetApy={storedData.includeRewardsInApy}
              className="font-bold text-primary-800 drop-shadow-md"
              hideTooltip
              period={period}
            />
          )
        }
      >
        <SupMint className={twMerge("drop-shadow-md", useHeader ? "h-10 w-10 text-neutral" : "h-4 w-4 text-primary-800", iconClass)} />
      </SupAssetLabel>
    </div>
  );

  return showTooltip ? (
    <SupTooltip
      unmount
      portal
      content={<UiLabelRewardsTooltip vault={vault} period={period} useFallback={useFallback} showCred />}
      popperElClassName="max-w-fit px-2 py-4 bg-neutral-25"
      color="default"
      placement={useHeader ? "left-start" : tooltipPlacement}
    >
      {linkElement}
    </SupTooltip>
  ) : (
    linkElement
  );
}
